/**
 * What a service's LABOR ROWS are worth — duration x rate, tier by tier.
 *
 * THERE IS NO SEPARATE LABOR COST RATE. A labor rate is what labor per unit of
 * time is worth, and it is the same number whichever way the service is priced.
 * The two modes differ only in what that number then DOES:
 *
 *   DYNAMIC  the service is priced FROM it — `hours x rate` is the labor term
 *            of the formula, so it moves what the customer pays.
 *   FLAT     the price is the number the operator typed, so the same
 *            `hours x rate` lands in COST and moves gross margin instead.
 *
 * See SPEC-line-item-model, "One rate — owner's ruling".
 *
 * Lives in `lib/sales` rather than beside the editor that reads it because it is
 * the answer to a money question, and `serviceCost` needs it without dragging a
 * React component into the costing path. `LaborLinesEditor` re-exports these so
 * every existing import keeps working.
 *
 * DURATION IS STORED IN MINUTES. Hours are `minutes / 60`, so `1h 30m` is 1.5
 * and is worth `1.5 x rate`. Nothing stores a third unit.
 */
import type {
  LaborRateProfile,
  ServiceLaborLine,
  DynamicServicePricingConfig,
} from '@/types/pricebook';

/** A blank row's shape, for reconstructing the one a legacy pair stands for. */
const RECONSTRUCTED_ROW: Omit<ServiceLaborLine, 'durationMinutes' | 'fixedRate'> = {
  label: '',
  rateSource: 'FIXED',
};

/**
 * The labor rows a flat service ACTUALLY has.
 *
 * Rows are the current form. An item saved before they existed carries only the
 * reduced pair — `estimatedHours` and `laborCostRate` — and one row stands for
 * it exactly: `hours x 60` minutes at that rate.
 *
 * ONE reconstruction, because there were two and they disagreed. The editor
 * rebuilt the row and showed the operator "8h @ $45"; the margin panel beside it
 * priced the raw `laborCostLines`, found none, and printed Labor $0.00 — one
 * screen stating both that the work takes eight hours and that it costs nothing,
 * until the operator happened to touch the rows. The comment on the editor's
 * copy said it was "the same reconstruction so the two never disagree", which is
 * the claim this makes true.
 *
 * Returns `[]` when there is genuinely no labor. An EDITOR wanting a blank row
 * to type into adds it itself — that is an editing affordance, not something the
 * service has, and a costing surface must not be handed one.
 */
export function flatServiceLaborLines(source: {
  laborCostLines?: ServiceLaborLine[] | null;
  estimatedHours?: number | null;
  laborCostRate?: number | null;
}): ServiceLaborLine[] {
  if (source.laborCostLines?.length) return source.laborCostLines;

  const hours = Number(source.estimatedHours ?? 0);
  if (!Number.isFinite(hours) || hours <= 0) return [];

  return [
    {
      ...RECONSTRUCTED_ROW,
      durationMinutes: Math.round(hours * 60),
      fixedRate: Number(source.laborCostRate ?? 0),
    },
  ];
}

/**
 * The fields a DOCUMENT line saves its labor rows as: the rows themselves, and
 * the reduced pair costing multiplies — total hours, and the blended rate over
 * them, so `hours x rate` comes back to exactly what the rows are worth even
 * when two crews work at two rates.
 *
 * One reduction for every document editor — the estimate's service row and the
 * job's line editor — so the rows an operator sees and the pair the margin is
 * computed from cannot disagree. With no hours there is no rate to state, and 0
 * is correct rather than a division by zero.
 */
export function laborCostFields(
  lines: ServiceLaborLine[],
  hours: number,
  cost: number,
): { laborCostLines: ServiceLaborLine[]; estimatedHours: number; laborCostRate: number } {
  const estimatedHours = Math.max(0, hours);
  return {
    laborCostLines: lines,
    estimatedHours,
    laborCostRate: estimatedHours > 0 ? cost / estimatedHours : 0,
  };
}

/** Total minutes across the lines, which is the stored unit. */
export function laborLinesMinutes(lines: ServiceLaborLine[]): number {
  return lines.reduce((total, line) => total + Math.max(0, line.durationMinutes || 0), 0);
}

/** Total hours — `minutes / 60`, so `1h 30m` is 1.5 and costs 1.5 x rate. */
export function laborLinesHours(lines: ServiceLaborLine[]): number {
  return laborLinesMinutes(lines) / 60;
}

/**
 * WHICH rate a labor line charges, named — the one wording, for every wizard
 * that draws one.
 *
 * There were three, and they disagreed. Two of them fell back to "Standard
 * rate" for any line without a resolved profile, including a rate typed by
 * hand, which is not standard and not a profile — and there is no standard rate
 * in this product. A third had been corrected to "Custom rate", so the same
 * labor line introduced itself differently on either side of "Edit
 * configuration" and again in the pricebook's own setup dialog.
 *
 * A FIXED line is named for what it is rather than for the profile it has none
 * of. `resolvedRateName` is whatever the catalog's preview called it at
 * materialization, and it does not describe a rate the operator has since
 * retyped.
 *
 * Beside `laborLineAmount` and taking the same `(line, profiles)`, because it
 * is the same question — what does this hour charge — answered as words rather
 * than as money, and the two must not be able to name different rates. Pass the
 * profiles a surface has; empty is a legitimate answer, and unlike costing it
 * cannot go quietly wrong, because the fallback is the frozen NAME rather than
 * a silent zero.
 */
export function laborRateLabel(line: ServiceLaborLine, profiles: LaborRateProfile[]): string {
  if (line.rateSource === 'FIXED') return 'Custom rate';
  const marginPercent = marginPercentFor(line, profiles);
  if (marginPercent != null) {
    const name =
      line.resolvedRateName ??
      profiles.find((profile) => profile.id === line.laborRateProfileId)?.name;
    // The percentage is the rate, so it is named beside the profile rather than
    // left to a dropdown the reader may not have open.
    return name ? `${name} (${marginPercent}% of margin)` : `${marginPercent}% of margin`;
  }
  const profileName =
    line.resolvedRateName ??
    profiles.find((profile) => profile.id === line.laborRateProfileId)?.name;
  if (profileName) return profileName;
  // A row stored before `rateSource` existed names no profile and carries only
  // a rate. It is a hand-typed rate and always was, so calling it a profile
  // would be the same mislabelling in the other direction.
  return line.laborRateProfileId ? 'Rate profile' : 'Custom rate';
}

/**
 * The rows with each margin share's CURRENT percentage frozen onto it.
 *
 * Called wherever a catalog rate becomes a DOCUMENT's rate — a service picked
 * onto a document, and a row edited on one — the same moment, and for the same
 * reason, that a dynamic service's tiers are frozen into `resolvedTiers`. A
 * document's costing passes no profiles (`document-costing`), so a share that
 * arrives unfrozen costs nothing: the Labor $0.00 a freshly picked service
 * showed beside a pricebook page reading 18% of margin.
 *
 * The catalog row deliberately does NOT carry this: there the live profile is
 * the answer, and editing a profile is supposed to restate what the catalog
 * service costs. Hourly rows come back untouched.
 */
export function freezeMarginShares(
  lines: ServiceLaborLine[],
  profiles: LaborRateProfile[],
): ServiceLaborLine[] {
  return lines.map((line) => {
    const profile = profiles.find((candidate) => candidate.id === line.laborRateProfileId);
    return profile?.kind === 'MARGIN_PERCENT'
      ? {
          ...line,
          resolvedMarginPercent: profile.marginPercent ?? 0,
          resolvedRateName: profile.name,
        }
      : line;
  });
}

/**
 * The share of margin this labor takes, or `null` when it is priced by the hour.
 *
 * THE FROZEN FIGURE WINS. A materialized document line carries the percentage
 * it was sold at in `resolvedMarginPercent`, exactly as it carries the tiers it
 * was sold at in `resolvedTiers`; a profile edited next quarter must not restate
 * what last quarter's estimate earned. The live profile is the fallback, for a
 * row still being edited in the catalog where there is nothing frozen yet.
 *
 * `null` rather than `0` for an hourly row, because 0 is a legitimate answer to
 * a different question and callers branch on which kind of row this is.
 */
export function marginPercentFor(
  line: ServiceLaborLine,
  profiles: LaborRateProfile[],
): number | null {
  const frozen = line.resolvedMarginPercent;
  if (typeof frozen === 'number' && Number.isFinite(frozen)) return frozen;

  if (line.rateSource !== 'PROFILE' || !line.laborRateProfileId) return null;
  const profile = profiles.find((candidate) => candidate.id === line.laborRateProfileId);
  if (!profile || profile.kind !== 'MARGIN_PERCENT') return null;

  const percent = profile.marginPercent;
  return typeof percent === 'number' && Number.isFinite(percent) ? percent : null;
}

/**
 * The rows, told apart by how they are paid — the ONE predicate.
 *
 * The editor's exclusivity rule and the costing must agree about which kind a
 * row is, or a service can be saved in a state the costing then prices twice.
 * They read this.
 */
/**
 * Which way a row is paid, INCLUDING "not decided yet".
 *
 * A row that has chosen a profile but not yet which one is neither hourly nor a
 * margin share — it is a blank the operator is still filling in. Counting it as
 * hourly (the safe-looking default) is what would stop a second margin-share row
 * ever being added: the blank row would itself be the hourly row that blocks the
 * margin profiles from appearing in its own dropdown.
 */
export function laborLineKind(
  line: ServiceLaborLine,
  profiles: LaborRateProfile[],
): 'MARGIN_SHARE' | 'HOURLY' | 'UNSET' {
  if (marginPercentFor(line, profiles) != null) return 'MARGIN_SHARE';
  if (line.rateSource === 'PROFILE' && !line.laborRateProfileId) return 'UNSET';
  return 'HOURLY';
}

export function splitLaborLines(
  lines: ServiceLaborLine[],
  profiles: LaborRateProfile[],
): { hourly: ServiceLaborLine[]; marginShare: ServiceLaborLine[] } {
  const hourly: ServiceLaborLine[] = [];
  const marginShare: ServiceLaborLine[] = [];
  for (const line of lines) {
    if (marginPercentFor(line, profiles) != null) marginShare.push(line);
    else hourly.push(line);
  }
  return { hourly, marginShare };
}

/**
 * The rate profiles a given labor row is ALLOWED to choose from.
 *
 * ONE KIND OF LABOR PER SERVICE, enforced in BOTH directions. The rule was first
 * written as "hide the shares when there are hours", which reads as symmetric and
 * is not: the hourly profiles stayed in the dropdown of a service already paying
 * a share, so "Apprentice / helper — $65/hr" could be put on the row below
 * "Crew share — 18% of margin" and produce exactly the mix the rule exists to
 * prevent. A service in that state totals to neither answer.
 *
 * Lives here, beside the arithmetic, because the two editors that draw this
 * dropdown must offer the same profiles. While the decision was inline in each
 * of them, only one of them got it right.
 *
 * THE ROW'S OWN PROFILE IS ALWAYS OFFERED, whatever kind it is. A dropdown that
 * omits its own current value renders blank and reads as data loss — and it is
 * how an already-mixed service (saved through the API, or predating this rule)
 * would quietly lose the rate it was quoting.
 */
export function selectableLaborProfiles(
  lines: ServiceLaborLine[],
  index: number,
  profiles: LaborRateProfile[],
  options?: { marginShareAllowed?: boolean },
): LaborRateProfile[] {
  const marginShareAllowed = options?.marginShareAllowed !== false;
  const othersInclude = (kind: 'HOURLY' | 'MARGIN_SHARE') =>
    lines.some((row, i) => i !== index && laborLineKind(row, profiles) === kind);

  const current = lines[index]?.laborRateProfileId;
  return profiles.filter((profile) => {
    if (profile.id === current) return true;
    if (profile.kind === 'MARGIN_PERCENT') {
      // A dynamic service is priced FROM its labor, so paying that labor a share
      // of the price would be circular — see `LaborLinesEditor`.
      if (!marginShareAllowed) return false;
      return !othersInclude('HOURLY');
    }
    return !othersInclude('MARGIN_SHARE');
  });
}

/**
 * Is this row a margin share — or bound to become one?
 *
 * A row that has not chosen its profile yet is neither kind on its own
 * (`laborLineKind`), but on a service that already pays a share it has nothing
 * else it CAN become: hourly options are withdrawn from its dropdown. Drawing it
 * with a duration and an hourly rate offered the operator two inputs the row
 * was never going to use, so it is drawn as the share it must be.
 */
export function rowTakesMarginShare(
  lines: ServiceLaborLine[],
  index: number,
  profiles: LaborRateProfile[],
): boolean {
  const line = lines[index];
  if (!line) return false;
  const kind = laborLineKind(line, profiles);
  if (kind !== 'UNSET') return kind === 'MARGIN_SHARE';
  return lines.some((row, i) => i !== index && laborLineKind(row, profiles) === 'MARGIN_SHARE');
}

/**
 * A rate dropdown's profiles in the order they are offered: margin shares
 * first, then the hourly profiles, so each editor can draw a divider between
 * the two kinds. Order within each kind is kept.
 */
export function laborProfilesByKind(profiles: LaborRateProfile[]): {
  shares: LaborRateProfile[];
  hourly: LaborRateProfile[];
} {
  return {
    shares: profiles.filter((profile) => profile.kind === 'MARGIN_PERCENT'),
    hourly: profiles.filter((profile) => profile.kind !== 'MARGIN_PERCENT'),
  };
}

/**
 * May this row still be billed at a plain hourly rate typed by hand?
 *
 * The other half of the same rule: a fixed rate is an HOURLY answer, so it is
 * withdrawn once the service pays shares — unless this row is the one already
 * using it, which stays readable for the reason above.
 */
export function fixedRateAllowedFor(
  lines: ServiceLaborLine[],
  index: number,
  profiles: LaborRateProfile[],
): boolean {
  if (lines[index]?.rateSource === 'FIXED') return true;
  return !lines.some((row, i) => i !== index && laborLineKind(row, profiles) === 'MARGIN_SHARE');
}

/**
 * What a service's margin-share rows take, given what there is to take it from.
 *
 * `contribution` is the service's price less its materials and equipment — what
 * is there to pay labor out of. The share is taken from that and never from the
 * price, or the crew would be paid for money the parts already spent.
 *
 * NEGATIVE CONTRIBUTION PAYS NOTHING. A service selling below its parts cost has
 * no margin to share, and a negative labor cost would flatter the very margin
 * this line exists to reduce — the loss would read as profit.
 *
 * SEVERAL ROWS ADD UP, each against the SAME contribution: two crews on 10% and
 * 8% take 18% between them, not 10% and then 8% of what is left. They are shares
 * of one job's margin, not a sequence of deductions, and making them sequential
 * would mean the order the operator happened to add them in changed what each
 * crew was paid.
 */
export function marginShareLaborTotal(
  lines: ServiceLaborLine[],
  profiles: LaborRateProfile[],
  contribution: number,
): number {
  const base = Math.max(0, contribution);
  return Math.round(base * marginSharePercentOf(lines, profiles)) / 100;
}

/**
 * The share of margin these rows take BETWEEN THEM — 10% and 8% are 18%, for
 * the reason `marginShareLaborTotal` gives. 0 when none of them is a share.
 */
export function marginSharePercentOf(
  lines: ServiceLaborLine[],
  profiles: LaborRateProfile[],
): number {
  return lines.reduce((total, line) => total + (marginPercentFor(line, profiles) ?? 0), 0);
}

/**
 * What one labor line is worth.
 *
 * One rate, one answer — `basis` is not consulted, on purpose. A profile is
 * TIERED because time is charged differently as a job runs long, and that is
 * true of the money whichever side of the margin it lands on.
 */
export function laborLineAmount(line: ServiceLaborLine, profiles: LaborRateProfile[]): number {
  // A margin-share row is not worth duration x rate and never was: it is worth
  // a share of what the service earns, which this function cannot see and must
  // not guess. `serviceCost` adds it, because that is where the price is.
  // Returning 0 here is the honest answer to the question actually asked.
  if (marginPercentFor(line, profiles) != null) return 0;

  const minutes = Math.max(0, line.durationMinutes || 0);
  if (line.rateSource === 'FIXED') return (minutes / 60) * Math.max(0, line.fixedRate ?? 0);

  const profile = profiles.find((candidate) => candidate.id === line.laborRateProfileId);
  if (!profile) return 0;

  let remaining = minutes;
  let amount = 0;
  for (const tier of profile.tiers ?? []) {
    const capacity =
      tier.toMinute == null ? remaining : Math.max(0, tier.toMinute - tier.fromMinute);
    const used = Math.min(remaining, capacity);
    amount += (used / 60) * tier.rate;
    remaining -= used;
    if (remaining <= 0) break;
  }
  return amount;
}

export function laborLinesTotal(lines: ServiceLaborLine[], profiles: LaborRateProfile[]): number {
  return lines.reduce((total, line) => total + laborLineAmount(line, profiles), 0);
}

/**
 * Are any of these hours being counted at nothing?
 *
 * Time with no rate behind it — a fixed rate of 0, or no profile chosen — makes
 * a flat job look more profitable than it is, so it is surfaced rather than
 * quietly costed at zero.
 */
export function laborLinesMissingRateMinutes(
  lines: ServiceLaborLine[],
  profiles: LaborRateProfile[],
): number {
  return lines.reduce((total, line) => {
    // A margin-share row is not sold by the hour, so hours it happens to carry
    // are not hours "counted at nothing" — warning about them would tell the
    // operator to set a rate the row does not have and does not want.
    if (marginPercentFor(line, profiles) != null) return total;
    const minutes = Math.max(0, line.durationMinutes || 0);
    if (minutes <= 0) return total;
    return laborLineAmount(line, profiles) > 0 ? total : total + minutes;
  }, 0);
}
